import React from 'react';
import Label from '@library/label';
import cn from 'classnames';
import { filterOptions, needsFilter, needsScroll, toggleOption, Option } from './options';
import './styles';

type Props = {
  options?: Option[];
  selected?: any[];
  onChange: (selected: any[]) => void;
  single?: boolean;
  label?: string;
  labelId?: string;
  description?: string;
  disabled?: boolean;
  cy?: string;
};

function PillGroup({
  options = [],
  selected = [],
  onChange,
  single = false,
  label,
  labelId,
  description,
  disabled,
  cy
}: Props) {
  const [query, setQuery] = React.useState('');
  const [expanded, setExpanded] = React.useState(false);

  const showFilter = needsFilter(options);
  const visible = showFilter ? filterOptions(options, query) : options;
  // Measured against what is actually on screen: a filter that genuinely narrows the
  // list leaves it uncapped, but one that still matches most of it keeps the cap
  const capsHeight = needsScroll(visible);
  const scrolls = capsHeight && !expanded;

  const toggle = (id) => {
    if (disabled) return;
    onChange(toggleOption(selected, id, single));
  };

  return (
    // Grouped and named so a screen reader announces which attribute the pills belong
    // to, rather than reading them as loose toggle buttons
    <div
      className="editor__pill-group"
      role="group"
      aria-labelledby={label && labelId ? labelId : undefined}
      aria-label={label && !labelId ? label : undefined}
      data-cy={cy || 'pill-group'}
    >
      <div className="editor__pill-group__heading">
        <Label
          id={labelId}
          show={Boolean(label)}
          text={label}
          description={description}
        />
        {selected.length > 0 && !disabled && (
          <button
            type="button"
            className="editor__pill-group__clear"
            data-cy="pill-group-clear"
            onClick={() => onChange([])}
          >
            Clear
          </button>
        )}
      </div>

      {showFilter && (
        <input
          className="editor__pill-group__filter"
          data-cy="pill-group-filter"
          type="text"
          value={query}
          disabled={disabled}
          placeholder={`Filter ${options.length} options`}
          onChange={(e) => setQuery(e.target.value)}
        />
      )}

      <div
        className={cn('editor__pill-group__options', {
          'editor__pill-group__options--scrolls': scrolls
        })}
      >
        {visible.map((option) => {
          const active = selected.indexOf(option.id) > -1;

          return (
            <button
              key={option.id}
              type="button"
              aria-pressed={active}
              disabled={disabled}
              data-cy="pill"
              className={cn('editor__pill-group__pill', {
                'editor__pill-group__pill--active': active
              })}
              onClick={() => toggle(option.id)}
            >
              {option.name}
            </button>
          );
        })}

        {visible.length === 0 && (
          <span className="editor__pill-group__empty">
            {`No option matches “${query}”`}
          </span>
        )}
      </div>

      {capsHeight && (
        <div className="editor__pill-group__footer">
          <button
            type="button"
            className="editor__pill-group__expand"
            data-cy="pill-group-expand"
            onClick={() => setExpanded((wasExpanded) => !wasExpanded)}
          >
            {expanded ? 'Collapse list' : `Show all ${visible.length}`}
          </button>
        </div>
      )}
    </div>
  );
}

export default PillGroup;
